import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Loader2, Globe, AlertTriangle, Plus, Trash2, X } from 'lucide-react';
import { apiService, LanguageConfigResponse } from '../../services/api';
import { toast } from 'sonner';

interface LanguageSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfigChange?: () => void;
}

export function LanguageSettingsDialog({
  open,
  onOpenChange,
  onConfigChange,
}: LanguageSettingsDialogProps) {
  const [languageConfig, setLanguageConfig] = useState<LanguageConfigResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newLangCode, setNewLangCode] = useState('');
  const [newLangLabel, setNewLangLabel] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  useEffect(() => {
    if (open) {
      loadLanguageConfig();
    }
  }, [open]);

  const loadLanguageConfig = async () => {
    try {
      setIsLoading(true);
      const config = await apiService.getLanguageConfig();
      setLanguageConfig(config);
    } catch (err: any) {
      console.error('Failed to load language config:', err);
      toast.error('Sprachkonfiguration konnte nicht geladen werden');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleLanguage = async (code: string, currentIsActive: boolean) => {
    // Prevent deactivating default language
    if (currentIsActive && languageConfig?.defaultLanguage === code) {
      toast.error('Die Standardsprache kann nicht deaktiviert werden');
      return;
    }

    try {
      setIsSaving(code);
      await apiService.toggleLanguage(code, !currentIsActive);
      await loadLanguageConfig();
      onConfigChange?.();
      toast.success(
        !currentIsActive
          ? `Sprache "${code.toUpperCase()}" aktiviert`
          : `Sprache "${code.toUpperCase()}" deaktiviert`
      );
    } catch (err: any) {
      console.error('Failed to toggle language:', err);
      toast.error(err.message || 'Sprache konnte nicht umgeschaltet werden');
    } finally {
      setIsSaving(null);
    }
  };

  const handleAddLanguage = async () => {
    if (!newLangCode.trim() || !newLangLabel.trim()) {
      toast.error('Bitte geben Sie Sprachcode und Label ein');
      return;
    }

    // Validate language code format
    if (!/^[a-z]{2,3}(-[A-Z]{2})?$/.test(newLangCode)) {
      toast.error('Ungültiges Sprachcode-Format. Verwenden Sie ISO 639-1 (z.B. "nl") oder mit Region (z.B. "pt-BR")');
      return;
    }

    try {
      setIsAdding(true);
      await apiService.addCustomLanguage(newLangCode.toLowerCase(), newLangLabel, true);
      await loadLanguageConfig();
      onConfigChange?.();
      toast.success(`Sprache "${newLangLabel}" hinzugefügt`);
      setNewLangCode('');
      setNewLangLabel('');
      setShowAddForm(false);
    } catch (err: any) {
      console.error('Failed to add language:', err);
      toast.error(err.message || 'Sprache konnte nicht hinzugefügt werden');
    } finally {
      setIsAdding(false);
    }
  };

  const handleRemoveLanguage = async (code: string, label: string) => {
    // Cannot remove default language
    if (languageConfig?.defaultLanguage === code) {
      toast.error('Die Standardsprache kann nicht entfernt werden');
      return;
    }

    try {
      setIsSaving(code);
      await apiService.removeCustomLanguage(code);
      await loadLanguageConfig();
      onConfigChange?.();
      toast.success(`Sprache "${label}" entfernt`);
    } catch (err: any) {
      console.error('Failed to remove language:', err);
      // If it's a built-in language that can't be removed, just show a warning
      if (err.message?.includes('built-in')) {
        toast.warning('Diese Sprache kann nicht entfernt, aber deaktiviert werden');
      } else {
        toast.error(err.message || 'Sprache konnte nicht entfernt werden');
      }
    } finally {
      setIsSaving(null);
    }
  };

  const isDefaultLanguage = (code: string): boolean => {
    return languageConfig?.defaultLanguage === code;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Globe className="w-5 h-5" />
            Spracheinstellungen
          </DialogTitle>
          <DialogDescription>
            Aktivieren oder deaktivieren Sie Sprachen für Übersetzungen
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
            <span className="ml-2 text-sm text-gray-600">Lade Sprachkonfiguration...</span>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="text-sm text-gray-500 mb-4">
              Fügen Sie Sprachen hinzu und aktivieren oder deaktivieren Sie diese für Übersetzungen.
            </div>

            {/* Add Language Button / Form */}
            <div className="space-y-2">
              {!showAddForm ? (
                <Button
                  variant="outline"
                  onClick={() => setShowAddForm(true)}
                  className="w-full"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Sprache hinzufügen
                </Button>
              ) : (
                <div className="p-3 rounded-lg border border-blue-200 bg-blue-50 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-blue-700">Neue Sprache hinzufügen</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setShowAddForm(false);
                        setNewLangCode('');
                        setNewLangLabel('');
                      }}
                      className="h-6 w-6 p-0"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Input
                        placeholder="Code (z.B. nl)"
                        value={newLangCode}
                        onChange={(e) => setNewLangCode(e.target.value.toLowerCase())}
                        maxLength={5}
                        className="h-8 text-sm"
                      />
                    </div>
                    <div>
                      <Input
                        placeholder="Label (z.B. Nederlands)"
                        value={newLangLabel}
                        onChange={(e) => setNewLangLabel(e.target.value)}
                        className="h-8 text-sm"
                      />
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={handleAddLanguage}
                    disabled={isAdding || !newLangCode.trim() || !newLangLabel.trim()}
                    className="w-full"
                  >
                    {isAdding ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        Hinzufügen...
                      </>
                    ) : (
                      <>
                        <Plus className="h-4 w-4 mr-1" />
                        Sprache hinzufügen
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>

            {/* Languages List */}
            <div className="space-y-2">
              {languageConfig?.languages && languageConfig.languages.length > 0 ? (
                languageConfig.languages.map((lang) => (
                  <div
                    key={lang.code}
                    className={`flex items-center justify-between p-3 rounded-lg border ${
                      lang.isActive ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="font-medium text-sm uppercase w-8">{lang.code}</span>
                      <span className="text-sm text-gray-700">{lang.label}</span>
                      {isDefaultLanguage(lang.code) && (
                        <Badge variant="secondary" className="text-xs">
                          Standard
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {isSaving === lang.code && (
                        <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                      )}
                      <Switch
                        checked={lang.isActive}
                        onCheckedChange={() => handleToggleLanguage(lang.code, lang.isActive)}
                        disabled={isSaving !== null || (lang.isActive && isDefaultLanguage(lang.code))}
                      />
                      {!isDefaultLanguage(lang.code) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveLanguage(lang.code, lang.label)}
                          disabled={isSaving !== null}
                          className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-400 italic py-4 text-center">
                  Keine Sprachen konfiguriert. Fügen Sie Sprachen hinzu.
                </p>
              )}
            </div>

            {/* Info box about default language */}
            {languageConfig?.defaultLanguage && (
              <div className="flex items-start gap-2 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                <AlertTriangle className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-yellow-700">
                  Die Standardsprache ({languageConfig.defaultLanguage.toUpperCase()}) kann nicht
                  deaktiviert oder entfernt werden.
                </p>
              </div>
            )}

            <div className="flex justify-end pt-4">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Schließen
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
